import {
  Box,
  CardMedia,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

/*
======================================================
 CITY FARM - WALLET / INVENTORY MODERNO
 - Mantiene el mismo export: Wallet
 - Sigue leyendo informacion.inventario desde Redux
 - Mejora visual completa
 - Buscador
 - Filtros por categoría
 - Totales rápidos
 - Diseño responsive
 - No rompe la lógica original
======================================================
*/

const CATEGORY_CONFIG = {
  resources: {
    label: "Resources",
    emoji: "🧱",
    keywords: ["water", "wood", "carbon", "energy", "agua", "madera", "energia"],
  },
  crops: {
    label: "Crops",
    emoji: "🌾",
    keywords: ["corn", "wheat", "maiz", "maíz", "trigo"],
  },
  bags: {
    label: "Bags",
    emoji: "🎒",
    keywords: ["bag", "saco", "corn bag", "wheat bag"],
  },
  seeds: {
    label: "Seeds",
    emoji: "🌱",
    keywords: ["seed", "semilla"],
  },
  animals: {
    label: "Animal Products",
    emoji: "🐄",
    keywords: ["egg", "milk", "huevo", "leche"],
  },
  pet: {
    label: "Pet",
    emoji: "🐶",
    keywords: ["croquette", "croquettes", "croqueta", "croquetas"],
  },
  others: {
    label: "Others",
    emoji: "📦",
    keywords: [],
  },
};

function safeNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function formatNumber(value) {
  const n = safeNumber(value);

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 3,
  }).format(n);
}

function getCategory(itemName = "") {
  const name = String(itemName || "").toLowerCase();

  for (const [key, cfg] of Object.entries(CATEGORY_CONFIG)) {
    if (key === "others") continue;

    if (cfg.keywords.some((word) => name.includes(word))) {
      return key;
    }
  }

  return "others";
}

function getItemAccent(itemName = "") {
  const name = String(itemName || "").toLowerCase();

  if (name.includes("water") || name.includes("agua")) {
    return {
      color: "#66eaff",
      glow: "rgba(102,234,255,0.28)",
      gradient: "linear-gradient(180deg, rgba(38,129,170,.95), rgba(12,62,94,.95))",
    };
  }

  if (name.includes("energy") || name.includes("energia")) {
    return {
      color: "#ffe65a",
      glow: "rgba(255,230,90,0.30)",
      gradient: "linear-gradient(180deg, rgba(180,145,20,.95), rgba(91,70,10,.95))",
    };
  }

  if (name.includes("carbon")) {
    return {
      color: "#d7d7d7",
      glow: "rgba(220,220,220,0.18)",
      gradient: "linear-gradient(180deg, rgba(70,70,76,.95), rgba(28,28,34,.95))",
    };
  }

  if (name.includes("wood") || name.includes("madera")) {
    return {
      color: "#d99a55",
      glow: "rgba(217,154,85,0.22)",
      gradient: "linear-gradient(180deg, rgba(123,77,38,.95), rgba(65,38,20,.95))",
    };
  }

  if (name.includes("corn") || name.includes("maiz") || name.includes("maíz")) {
    return {
      color: "#ffe36a",
      glow: "rgba(255,227,106,0.20)",
      gradient: "linear-gradient(180deg, rgba(139,116,23,.95), rgba(82,69,13,.95))",
    };
  }

  if (name.includes("wheat") || name.includes("trigo")) {
    return {
      color: "#ffd48a",
      glow: "rgba(255,212,138,0.20)",
      gradient: "linear-gradient(180deg, rgba(129,91,32,.95), rgba(74,50,18,.95))",
    };
  }

  if (name.includes("egg") || name.includes("milk") || name.includes("huevo") || name.includes("leche")) {
    return {
      color: "#ffffff",
      glow: "rgba(255,255,255,0.20)",
      gradient: "linear-gradient(180deg, rgba(112,105,90,.95), rgba(54,50,45,.95))",
    };
  }

  if (name.includes("croquette") || name.includes("croqueta")) {
    return {
      color: "#ffbd66",
      glow: "rgba(255,189,102,0.26)",
      gradient: "linear-gradient(180deg, rgba(154,82,27,.95), rgba(87,42,14,.95))",
    };
  }

  return {
    color: "#9dffb0",
    glow: "rgba(157,255,176,0.18)",
    gradient: "linear-gradient(180deg, rgba(52,104,54,.95), rgba(25,58,31,.95))",
  };
}

export const Wallet = ({ handleClose }) => {
  const { informacion } = useSelector((state) => state.userSlice);

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const inventory = useMemo(() => {
    const raw = Array.isArray(informacion?.inventario) ? informacion.inventario : [];

    return raw
      .filter((item) => item && item.item)
      .map((item) => ({
        ...item,
        cantidad: safeNumber(item.cantidad),
        category: getCategory(item.item),
      }))
      .sort((a, b) => {
        const amountDiff = safeNumber(b.cantidad) - safeNumber(a.cantidad);
        if (amountDiff !== 0) return amountDiff;
        return String(a.item || "").localeCompare(String(b.item || ""));
      });
  }, [informacion]);

  const filteredInventory = useMemo(() => {
    const q = search.trim().toLowerCase();

    return inventory.filter((item) => {
      const name = String(item.item || "").toLowerCase();

      if (activeCategory !== "all" && item.category !== activeCategory) {
        return false;
      }

      if (q && !name.includes(q)) {
        return false;
      }

      return true;
    });
  }, [inventory, search, activeCategory]);

  const summary = useMemo(() => {
    const totalItems = inventory.length;
    const totalQuantity = inventory.reduce((sum, item) => sum + safeNumber(item.cantidad), 0);
    const zeroItems = inventory.filter((item) => safeNumber(item.cantidad) <= 0).length;

    const categories = Object.keys(CATEGORY_CONFIG).reduce((acc, key) => {
      acc[key] = inventory.filter((item) => item.category === key).length;
      return acc;
    }, {});

    return {
      totalItems,
      totalQuantity,
      zeroItems,
      categories,
    };
  }, [inventory]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100%",
        color: "white",
        overflow: "hidden",
        background:
          "radial-gradient(circle at 50% 0%, rgba(255,214,112,.10), transparent 36%)," +
          "linear-gradient(180deg, rgba(35,27,36,.98), rgba(20,18,24,.98))",
      }}
    >
      {/* FX + estilos internos */}
      <style>
        {`
          @keyframes walletGoldSweep {
            0% { transform: translateX(-140%) skewX(-18deg); opacity: 0; }
            20% { opacity: .9; }
            60% { opacity: .75; }
            100% { transform: translateX(240%) skewX(-18deg); opacity: 0; }
          }

          @keyframes walletFloat {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
          }

          @keyframes walletPulse {
            0%, 100% { box-shadow: 0 0 0 rgba(255,218,105,0); }
            50% { box-shadow: 0 0 24px rgba(255,218,105,.20); }
          }

          @keyframes walletScanline {
            0% { transform: translateY(-100%); opacity: 0; }
            20% { opacity: .12; }
            100% { transform: translateY(120%); opacity: 0; }
          }
        `}
      </style>

      {/* Overlay scanline */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.6,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(transparent, rgba(255,255,255,.035), transparent)",
            animation: "walletScanline 5s linear infinite",
          },
        }}
      />

      {/* Close */}
      <Tooltip title="Close inventory" arrow>
        <Box
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 10,
            zIndex: 10000000000001,
            width: 34,
            height: 34,
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            color: "#fff",
            fontWeight: 900,
            borderRadius: "10px",
            background: "rgba(0,0,0,.55)",
            border: "2px solid rgba(255,218,105,.8)",
            boxShadow: "0 4px 0 rgba(0,0,0,.35)",
            transition: "transform .12s ease, filter .12s ease",
            "&:hover": {
              transform: "scale(1.05)",
              filter: "brightness(1.12)",
            },
          }}
        >
          X
        </Box>
      </Tooltip>

      <Box sx={{ position: "relative", zIndex: 2, p: { xs: 1.2, sm: 2 } }}>
        {/* Header */}
        <Box
          className="rpgui-container framed-golden"
          sx={{
            position: "relative !important",
            overflow: "hidden",
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            background:
              "linear-gradient(180deg, rgba(122,76,40,.96), rgba(80,48,32,.96)) !important",
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-40%",
              left: "-60%",
              width: "36%",
              height: "180%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,180,.18), rgba(255,240,130,.65), rgba(255,255,180,.18), transparent)",
              animation: "walletGoldSweep 3.6s linear infinite",
              pointerEvents: "none",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "#ffe28a !important",
                  fontSize: { xs: "1.2rem !important", sm: "1.7rem !important" },
                  fontWeight: "900 !important",
                  textShadow: "2px 3px 0 #000",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                🎒 Inventory
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: "rgba(255,255,255,.85) !important",
                  fontSize: "0.75rem !important",
                  textShadow: "1px 2px 0 rgba(0,0,0,.55)",
                }}
              >
                Manage and review all your City Farm resources.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Chip
                label={`Items: ${summary.totalItems}`}
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  background: "rgba(0,0,0,.45)",
                  border: "1px solid rgba(255,255,255,.12)",
                }}
              />
              <Chip
                label={`Total qty: ${formatNumber(summary.totalQuantity)}`}
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  background: "rgba(0,0,0,.45)",
                  border: "1px solid rgba(255,255,255,.12)",
                }}
              />
              <Chip
                label={`Empty: ${summary.zeroItems}`}
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  background: "rgba(0,0,0,.45)",
                  border: "1px solid rgba(255,255,255,.12)",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Search + filters */}
        <Box
          sx={{
            mb: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 360px) 1fr" },
            gap: 1.3,
            alignItems: "center",
          }}
        >
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
            placeholder="Search item..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span style={{ color: "#ffe28a" }}>🔎</span>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                color: "#fff",
                fontWeight: 900,
                background: "rgba(0,0,0,.45)",
                borderRadius: "14px",
                boxShadow: "inset 0 0 0 2px rgba(255,255,255,.08)",
              },
              "& input::placeholder": {
                color: "rgba(255,255,255,.65)",
                opacity: 1,
              },
              "& fieldset": {
                border: "0 !important",
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 0.8,
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Button
              onClick={() => setActiveCategory("all")}
              sx={{
                color: activeCategory === "all" ? "#1b1209" : "#fff",
                fontWeight: 900,
                borderRadius: "999px",
                px: 1.5,
                py: 0.6,
                background:
                  activeCategory === "all"
                    ? "linear-gradient(180deg, #ffe28a, #d88422)"
                    : "rgba(0,0,0,.45)",
                boxShadow: "inset 0 0 0 2px rgba(255,255,255,.08)",
                "&:hover": { filter: "brightness(1.08)" },
              }}
            >
              All
            </Button>

            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
              <Button
                key={key}
                onClick={() => setActiveCategory(key)}
                sx={{
                  color: activeCategory === key ? "#1b1209" : "#fff",
                  fontWeight: 900,
                  borderRadius: "999px",
                  px: 1.5,
                  py: 0.6,
                  background:
                    activeCategory === key
                      ? "linear-gradient(180deg, #9dffb0, #30b94d)"
                      : "rgba(0,0,0,.45)",
                  boxShadow: "inset 0 0 0 2px rgba(255,255,255,.08)",
                  "&:hover": { filter: "brightness(1.08)" },
                }}
              >
                {cfg.emoji} {cfg.label} ({summary.categories[key] || 0})
              </Button>
            ))}
          </Box>
        </Box>

        {/* Empty state */}
        {!filteredInventory.length && (
          <Box
            className="rpgui-container framed-golden"
            sx={{
              p: 4,
              textAlign: "center",
              background: "rgba(0,0,0,.35) !important",
            }}
          >
            <Typography
              sx={{
                color: "#ffe28a !important",
                fontSize: "1.4rem !important",
                fontWeight: "900 !important",
                textShadow: "2px 3px 0 #000",
              }}
            >
              No items found
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "rgba(255,255,255,.78) !important",
                fontSize: "0.85rem !important",
              }}
            >
              Try another search or category.
            </Typography>
          </Box>
        )}

        {/* Inventory grid */}
        <Grid2 container spacing={1.5}>
          {filteredInventory.map((item) => {
            const accent = getItemAccent(item.item);
            const category = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.others;
            const qty = safeNumber(item.cantidad);
            const isEmpty = qty <= 0;

            return (
              <Grid2 xs={12} sm={6} md={4} lg={3} key={item?.item}>
                <Box
                  className="rpgui-container framed-golden"
                  sx={{
                    position: "relative !important",
                    minHeight: 168,
                    height: "100%",
                    p: "12px !important",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    background: `${accent.gradient} !important`,
                    opacity: isEmpty ? 0.72 : 1,
                    animation: isEmpty ? "none" : "walletPulse 3.2s ease-in-out infinite",
                    transition: "transform .14s ease, filter .14s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      filter: "brightness(1.08)",
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      background:
                        "radial-gradient(circle at 65% 30%, rgba(255,255,255,.18), transparent 32%)," +
                        `radial-gradient(circle at 50% 70%, ${accent.glow}, transparent 52%)`,
                      opacity: 0.9,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "-45%",
                      left: "-65%",
                      width: "42%",
                      height: "190%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,.10), rgba(255,255,255,.28), rgba(255,255,255,.10), transparent)",
                      transform: "skewX(-18deg)",
                      animation: "walletGoldSweep 5.2s linear infinite",
                      animationDelay: `${Math.random() * 1.2}s`,
                      pointerEvents: "none",
                    },
                  }}
                >
                  {/* top row */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "#fff !important",
                          fontSize: "0.85rem !important",
                          fontWeight: "900 !important",
                          textShadow: "2px 2px 0 rgba(0,0,0,.75)",
                          lineHeight: 1.15,
                          wordBreak: "break-word",
                        }}
                      >
                        {item.item}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.4,
                          color: "rgba(255,255,255,.74) !important",
                          fontSize: "0.62rem !important",
                          fontWeight: "800 !important",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {category.emoji} {category.label}
                      </Typography>
                    </Box>

                    <Chip
                      label={isEmpty ? "EMPTY" : "ACTIVE"}
                      size="small"
                      sx={{
                        color: isEmpty ? "#ffb5b5" : "#d9ffdd",
                        fontWeight: 900,
                        fontSize: "0.58rem",
                        background: "rgba(0,0,0,.48)",
                        border: "1px solid rgba(255,255,255,.12)",
                      }}
                    />
                  </Box>

                  {/* image */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      my: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 72,
                    }}
                  >
                    <CardMedia
                      className="rpgui-cursor-grab-open"
                      component="img"
                      image={item?.url}
                      alt={item?.item}
                      sx={{
                        width: "78px",
                        height: "78px",
                        objectFit: "contain",
                        imageRendering: "auto",
                        filter: isEmpty
                          ? "grayscale(100%) brightness(.8)"
                          : `drop-shadow(0 10px 8px rgba(0,0,0,.45)) drop-shadow(0 0 12px ${accent.glow})`,
                        animation: isEmpty ? "none" : "walletFloat 3s ease-in-out infinite",
                        userSelect: "none",
                      }}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </Box>

                  {/* quantity */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "12px",
                      background: "rgba(0,0,0,.42)",
                      border: "1px solid rgba(255,255,255,.10)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,.78) !important",
                        fontSize: "0.65rem !important",
                        fontWeight: "900 !important",
                        textTransform: "uppercase",
                      }}
                    >
                      Quantity
                    </Typography>

                    <Typography
                      sx={{
                        color: `${accent.color} !important`,
                        fontSize: "1rem !important",
                        fontWeight: "900 !important",
                        textShadow: "2px 2px 0 rgba(0,0,0,.7)",
                      }}
                    >
                      {formatNumber(qty)}
                    </Typography>
                  </Box>
                </Box>
              </Grid2>
            );
          })}
        </Grid2>
      </Box>
    </Box>
  );
};
